import { ILogger } from '@cian/logger/shared';
import { ITelemetryLogger } from '@cian/telemetry/shared';
import * as React from 'react';

import {
  detectYmapsScriptLoadErrorReason,
  findYmapsScriptSrc,
  TYmapsScriptLoadErrorReason,
} from './detectScriptLoadErrorReason';

interface IUseYmapsScriptLoadErrorParams {
  logger: ILogger;
  telemetry?: ITelemetryLogger | null;
  /** Префикс ключей телеметрии, например `v2-mf-infrastructure` */
  telemetryPrefix: string;
}

interface IUseYmapsScriptLoadErrorResult {
  /** Причина ошибки загрузки скрипта, null — ошибки не было или причина ещё определяется */
  reason: TYmapsScriptLoadErrorReason | null;
  /** Вызывается, когда сторонний пакет не смог загрузить скрипт Яндекс карт */
  handleError(): void;
}

/**
 * Определяет причину ошибки загрузки скрипта Яндекс карт, который вставляет сторонний пакет,
 * и отправляет её в логи и телеметрию
 */
export function useYmapsScriptLoadError({
  logger,
  telemetry,
  telemetryPrefix,
}: IUseYmapsScriptLoadErrorParams): IUseYmapsScriptLoadErrorResult {
  const [reason, setReason] = React.useState<TYmapsScriptLoadErrorReason | null>(null);
  const isHandledRef = React.useRef(false);

  const handleError = React.useCallback(() => {
    // Пакеты могут сообщить об одной ошибке несколько раз
    if (isHandledRef.current) {
      return;
    }

    isHandledRef.current = true;

    detectYmapsScriptLoadErrorReason(findYmapsScriptSrc(), detectedReason => {
      logger.error(new Error('Yandex maps script load error'), {
        domain: `${telemetryPrefix}.useYmapsScriptLoadError`,
        message: 'Не удалось загрузить скрипт Яндекс карт',
        reason: detectedReason,
      });
      telemetry?.increment(`${telemetryPrefix}.ymaps.script-load.error`);
      telemetry?.increment(`${telemetryPrefix}.ymaps.script-load.error.${detectedReason}`);

      setReason(detectedReason);
    });
  }, [logger, telemetry, telemetryPrefix]);

  return { reason, handleError };
}
